import { OnboardingData } from '../onboarding-data.interface';
import { PlatformTourStep } from './platform-tour.service';
import { PERSONAL_ONBOARDING_BOOKING_URL, getArsPersonalOnboardingOffer } from '../personal-onboarding';

export type TourLanguage = 'DE' | 'EN';

export { PERSONAL_ONBOARDING_BOOKING_URL };

export const PLATFORM_TOUR_PROMPTS_EN: string[] = [
  'I need two software engineer in Saarbrücken on short notice — full-time, with experience in Python. Start searching all available sources and provide me with a shortlist in 5 minutes.',
  'Conduct an initial AI interview with all candidates for the Sales Manager position. Evaluate them based on communication, experience, and salary expectations. Present the results as a ranking.',
  'Schedule a 30-minute introductory call with each of the top three candidates — this week, on Tuesday or Thursday, starting at 10:00 AM. Automatically send the Teams link.',
  'Re-engage all candidates from my pool who weren\u2019t placed in the last 6 months and might fit my open DevOps Engineer position. Use a personalized message in their language.',
  'I haven\u2019t found a suitable candidate. Please search again — specifically on LinkedIn and in the MONA pool — for a Senior Backend Developer with Go expertise, at least 5 years of experience, based remotely or in Berlin.',
  'Create a job ad for a Senior Product Designer in Munich, hybrid, focus on B2B SaaS. Optimize it for LinkedIn and provide an English version.',
  'Benchmark salary expectations for a Senior Frontend Engineer (React, TypeScript) in Berlin against the current market and show me the typical range plus my position in it.',
  'Compare the top 5 candidates for the Marketing Lead position side by side based on skills, experience, location, notice period and salary expectations.',
  'Launch a LinkedIn outreach campaign for 50 Senior Data Engineers in DACH. Follow up twice if there\u2019s no reply and let interested candidates book directly into my calendar.',
  'Export the shortlist of the top 10 candidates for the COO search as a PDF with profile summaries and send it to my hiring manager Sarah by end of day.',
  'Send a friendly, personalized rejection email to all candidates of the closed Junior Recruiter position. Mention that we\u2019d like to stay in touch for future roles.',
  'Show me a weekly hiring report: open positions, candidates per stage, response rates and time-to-hire compared to last week. Highlight the bottlenecks.'
];

export const PLATFORM_TOUR_PROMPTS_DE: string[] = [
  'Ich brauche kurzfristig zwei Softwareentwickler in Saarbrücken — Vollzeit, mit Python-Erfahrung. Durchsuche alle verfügbaren Quellen und gib mir innerhalb von 5 Minuten eine Shortlist.',
  'Führe ein erstes KI-Interview mit allen Kandidaten für die Position Sales Manager durch. Bewerte sie nach Kommunikation, Erfahrung und Gehaltsvorstellung. Stelle die Ergebnisse als Ranking dar.',
  'Plane ein 30-minütiges Kennenlerngespräch mit jedem der drei besten Kandidaten — diese Woche, am Dienstag oder Donnerstag ab 10:00 Uhr. Versende den Teams-Link automatisch.',
  'Reaktiviere alle Kandidaten aus meinem Pool, die in den letzten 6 Monaten nicht vermittelt wurden und zur offenen DevOps-Engineer-Stelle passen könnten. Nutze eine personalisierte Nachricht in ihrer Sprache.',
  'Ich habe noch keinen passenden Kandidaten gefunden. Suche bitte erneut — speziell auf LinkedIn und im MONA-Pool — nach einem Senior Backend Developer mit Go-Kenntnissen, mindestens 5 Jahre Erfahrung, remote oder in Berlin.',
  'Erstelle eine Stellenanzeige für einen Senior Product Designer in München, hybrid, mit Fokus auf B2B-SaaS. Optimiere sie für LinkedIn und gib mir auch eine englische Version.',
  'Vergleiche die Gehaltsvorstellungen für einen Senior Frontend Engineer (React, TypeScript) in Berlin mit dem aktuellen Markt und zeige mir die übliche Spanne sowie meine Position darin.',
  'Vergleiche die Top-5-Kandidaten für die Marketing-Lead-Position nebeneinander nach Skills, Erfahrung, Standort, Kündigungsfrist und Gehaltsvorstellung.',
  'Starte eine LinkedIn-Outreach-Kampagne für 50 Senior Data Engineers in der DACH-Region. Hake zweimal nach, wenn keine Antwort kommt, und lass interessierte Kandidaten direkt in meinen Kalender buchen.',
  'Exportiere die Shortlist der Top-10-Kandidaten für die COO-Suche als PDF mit Profilzusammenfassungen und sende sie bis Tagesende an meinen Hiring Manager Sarah.',
  'Sende eine freundliche, personalisierte Absage-E-Mail an alle Kandidaten der geschlossenen Junior-Recruiter-Position. Erwähne, dass wir gerne für zukünftige Rollen in Kontakt bleiben möchten.',
  'Zeige mir einen Wochenbericht zum Recruiting: offene Positionen, Kandidaten pro Stage, Response-Raten und Time-to-Hire im Vergleich zur Vorwoche. Hebe die Engpässe hervor.'
];

export function getPlatformTourPrompts(language: TourLanguage): string[] {
  return language === 'EN' ? PLATFORM_TOUR_PROMPTS_EN : PLATFORM_TOUR_PROMPTS_DE;
}

export function buildPlatformTourSteps(
  _onboarding: OnboardingData | null,
  _isHybridCustomer: boolean = true,
  language: TourLanguage = 'DE',
  campaignId: string | null = null
): PlatformTourStep[] {
  const steps: PlatformTourStep[] = [];

  steps.push({
    id: 'welcome',
    placement: 'center',
    titleDe: 'Willkommen bei Mona AI',
    titleEn: 'Welcome to Mona AI',
    bodyDe: 'In drei kurzen Schritten richten wir Ihre Plattform produktiv ein: Kalender verbinden, LinkedIn fürs Sourcing aktivieren und Bewerber importieren. Danach zeigen wir Ihnen 12 Prompts, mit denen Sie sofort loslegen können.',
    bodyEn: 'In three quick steps we get your platform productive: connect the calendar, enable LinkedIn for sourcing and import applicants. After that we show you 12 ready-to-use prompts to start right away.',
    showSkip: true,
    scrollIntoView: false
  });

  steps.push({
    id: 'connect-calendar',
    route: '/hybrid-dashboard',
    selector: ['app-upcoming-widget', '[data-tour="home-calendar"]', '.widget-card'],
    placement: 'auto',
    spotlightPadding: 8,
    titleDe: 'Kalender verbinden',
    titleEn: 'Connect your calendar',
    bodyDe: 'Verbinden Sie Google oder Microsoft, damit Mona Interviews automatisch koordinieren, Teams-Links versenden und Termine direkt in Ihren Kalender legen kann.',
    bodyEn: 'Connect Google or Microsoft so Mona can schedule interviews automatically, send Teams links and place appointments directly in your calendar.'
  });

  steps.push({
    id: 'connect-linkedin',
    route: '/hybrid-dashboard',
    selector: ['[data-tour="rec-card-linkedin-connect"]', '.recommendations-section .rec-card'],
    placement: 'auto',
    spotlightPadding: 8,
    waitForSelectorMs: 4000,
    titleDe: 'LinkedIn fürs Sourcing aktivieren',
    titleEn: 'Enable LinkedIn for sourcing',
    bodyDe: 'Klicken Sie auf diese Empfehlungskarte, um LinkedIn als Sourcing-Quelle zu verbinden. Mona durchsucht LinkedIn dann vollautomatisch nach passenden Kandidaten und kontaktiert sie in Ihrem Stil.',
    bodyEn: 'Click this recommendation card to connect LinkedIn as a sourcing source. Mona then searches LinkedIn automatically for matching candidates and reaches out in your style.'
  });

  steps.push({
    id: 'import-applicants',
    route: '/upload',
    selector: ['.hybrid-dropzone', '.dropzone'],
    placement: 'auto',
    spotlightPadding: 8,
    titleDe: 'Bewerber importieren',
    titleEn: 'Import applicants',
    bodyDe: 'Importieren Sie Ihre bestehenden Bewerber per Excel, CSV oder PDF-Lebensläufen. Die KI extrahiert automatisch alle relevanten Daten und legt strukturierte Profile an.',
    bodyEn: 'Import your existing applicants via Excel, CSV or PDF resumes. The AI extracts all relevant data automatically and creates structured profiles.',
    ctaLabelDe: 'Upload öffnen',
    ctaLabelEn: 'Open upload',
    ctaRoute: '/upload'
  });

  steps.push({
    id: 'prompt-examples',
    kind: 'prompts',
    route: '/hybrid-dashboard',
    selector: ['.prompt-bar', '[data-tour="home-prompt"]'],
    placement: 'auto',
    spotlightPadding: 8,
    titleDe: '12 Prompts zum Loslegen',
    titleEn: '12 prompts to get started',
    bodyDe: 'Klicken Sie einen Prompt an, um ihn zu kopieren — fügen Sie ihn dann in die Eingabezeile oben ein und Mona startet sofort.',
    bodyEn: 'Click a prompt to copy it — then paste it into the input above and Mona starts right away.',
    prompts: getPlatformTourPrompts(language)
  });

  if (_isHybridCustomer) {
    steps.push({
      id: 'live-demo',
      kind: 'cta',
      heroIcon: 'play-circle-outline',
      placement: 'center',
      titleDe: 'Mona live erleben',
      titleEn: 'See Mona in action',
      bodyDe: 'Starten Sie eine kurze Demo und sehen Sie, wie Mona eine komplette Suche autonom durchführt — von der Kandidatensuche über die Vorauswahl bis zur automatischen Terminbuchung.',
      bodyEn: 'Start a short demo and watch Mona run a complete search autonomously — from sourcing candidates and shortlisting to automatically booking interviews.',
      ctaAction: 'ars-demo',
      ctaActionLabelDe: 'Demo starten',
      ctaActionLabelEn: 'Start demo',
      scrollIntoView: false
    });

    const offer = getArsPersonalOnboardingOffer(campaignId);
    steps.push({
      id: 'personal-onboarding',
      kind: 'cta',
      heroIcon: 'headset-outline',
      placement: 'center',
      titleDe: offer.titleDe,
      titleEn: offer.titleEn,
      bodyDe: offer.bodyDe,
      bodyEn: offer.bodyEn,
      ctaUrl: PERSONAL_ONBOARDING_BOOKING_URL,
      ctaUrlLabelDe: offer.ctaLabelDe,
      ctaUrlLabelEn: offer.ctaLabelEn,
      scrollIntoView: false
    });
  }

  steps.push({
    id: 'finish',
    placement: 'center',
    titleDe: 'Bereit für Ihre erste Suche',
    titleEn: 'Ready for your first search',
    bodyDe: 'Sie haben das Wichtigste eingerichtet. Starten Sie jetzt mit einem der Prompts oder formulieren Sie Ihre eigene Anfrage.',
    bodyEn: 'You\u2019re set up. Start now with one of the prompts or write your own request.',
    ctaLabelDe: 'Zum Dashboard',
    ctaLabelEn: 'Go to dashboard',
    ctaRoute: '/hybrid-dashboard',
    scrollIntoView: false
  });

  return steps;
}
